/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import Modal from '../common/Modal'
import { dateToTodayElapsedSeconds, diffNowToDate, format, nowDate, secToHour, secToMinute } from '../../../utils/date'
import Tips from '../common/Tips'
import { sortBy } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import AddLabelBigModal from './AddLabelBigModal'
import SelectAndInput from '../common/SelectAndInput'

interface LabelList {
  label_id: number
  label_name: string
  label_color: string
  description: string | null
  disabled?: boolean
}

interface LabelGroupList {
  label_group_id: number
  label_group_name: string
  label_group_color?: string
  disabled?: boolean
  labels: LabelList[]
}

interface TaskDetail {
  task_id: number
  type: string
  time: number
  service_name: string | null
  icon: string | null
  icon_color: string | null
  task_name: string | null
  file_path: string | null
  timeline: {
    start_time: string
    end_time: string
  }[]
  label: {
    label_group_id: number
    label_group_name: string
    label_group_color: string
    label_id: number
    label_name: string
    label_color: string
    disabled: boolean
    status: string
  } | null
  is_loading?: boolean
}

interface FormatOther {
  start_sec: number;
  end_sec: number;
  service_name: string;
  icon: string | null;
  icon_color: string | null;
  start_time: string;
  end_time: string;
}

interface LabelCommon {
  id: number
  name: string
  code: string | null
  color: string
}

interface Props {
  type: string
  timeList: {
    startSec: number,
    endSec: number,
  }[],
  originTask: TaskDetail | undefined
  cutCallback: (
    name: string | null,
    labelId: number | null,
    timeline: {
      startTime: number,
      endTime: number,
    }[],
    cutTimeList: {
      startSec: number,
      endSec: number,
    }[],
    isNotBelong: boolean,
    originTaskId: number | null,
    type: string,
    afterFunc?: () => void,
  ) => void
  closeCallback: VoidFunction
  isDisplay: boolean
  currentMemberId: number
  date: string
  overwrite: boolean
}

const CutTaskModal: FC<Props> = (props) => {
  const {
    type,
    timeList,
    originTask,
    cutCallback,
    closeCallback,
    isDisplay,
    currentMemberId,
    date,
    overwrite,
  } = props

  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const TIME_LIST = Array(25).fill(null).map((_v, i) => `${i + 5}:00`)

  const [cutTimeList, setCutTimeList] = useState<{startSec: number, endSec: number}[]>([{ startSec: 36000, endSec: 50400 }])

  const [isMoveDrag, setIsMoveDrag] = useState<boolean>(false)

  const [name, setName] = useState<string | null>("手動登録作業データ")

  const [error, setError] = useState<{key: string, text: string}[]>([])

  const [selectLabel, setSelectLabel] = useState<{id: number | null, name: string}>({ id: null, name: "" })
  const [labelGroupListInit, setLabelGroupListInit] = useState<LabelGroupList[]>([])
  const [labelGroupList, setLabelGroupList] = useState<LabelGroupList[]>([])
  const [isDisplayLabelBox, setIsDisplayLabelBox] = useState<boolean>(false)

  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const [isChangeRecodeTime, setIsChangeRecodeTime] = useState<boolean>(false)

  const [isForceMode, setIsForceMode] = useState<boolean>(true)

  const [dragType, setDragType] = useState<string>("")
  const [basePosition, setBasePosition] = useState<number | null>(null)
  const [baseStartTime, setBaseStartTime] = useState<number>(0)
  const [baseEndTime, setBaseEndTime] = useState<number>(0)
  const [baseIndex, setBaseIndex] = useState<number | null>(null)

  const clickRef = useRef() as React.MutableRefObject<HTMLDivElement>

  const customOriginTask = useMemo(() => {
    if (!originTask) return []
    return originTask.timeline.map((v) => ({ ...v, start_sec: dateToTodayElapsedSeconds(v.start_time, date), end_sec: dateToTodayElapsedSeconds(v.end_time, date) }))
  }, [originTask])

  const customOtherAppList = useMemo(() => {
    if (!task.taskLabeling?.others) return []
    const others = task.taskLabeling?.others.map((v) => ({
      ...v,
      start_sec: dateToTodayElapsedSeconds(v.start_time, date),
      end_sec: dateToTodayElapsedSeconds(v.end_time, date),
    })) || []
    const tmpList: FormatOther[] = []
    others.forEach((v) => {
      customOriginTask.forEach((w) => {
        if (v.start_sec <= w.end_sec && v.end_sec >= w.start_sec) {
          let tmpStartSec = v.start_sec
          let tmpEndSec = v.end_sec
          if (v.start_sec < w.start_sec) {
            tmpStartSec = w.start_sec
          }
          if (v.end_sec > w.end_sec) {
            tmpEndSec = w.end_sec
          }
          tmpList.push({ ...v, start_sec: tmpStartSec, end_sec: tmpEndSec })
        }
      })
    })
    return tmpList
  }, [task.taskLabeling?.others, customOriginTask])

  const customCalendarSchedules = useMemo(() => {
    if (!task.taskLabeling?.calendar.schedules) return []
    return task.taskLabeling?.calendar.schedules.map((v) => ({ ...v, start_sec: dateToTodayElapsedSeconds(v.start_time, date), end_sec: dateToTodayElapsedSeconds(v.end_time, date) }))
  }, [task.taskLabeling?.calendar.schedules])

  const originTaskTotalTime = useMemo(() => {
    if (!customOriginTask) return 0
    return customOriginTask.filter((v) => v.end_sec - v.start_sec > 0).reduce((s, v) => s + (v.end_sec - v.start_sec), 0)
  }, [customOriginTask])

  const selectRecodeList = useMemo(() => {
    if (!originTask) return []
    const tmpList: { id: number, startTime: number, endTime: number }[] = []
    cutTimeList.forEach((cutTime, index) => {
      customOriginTask.filter((v) => (v.end_sec - v.start_sec) > 0).forEach((v, i) => {
        if (!(cutTime.startSec < v.end_sec && v.start_sec < cutTime.endSec)) return
        tmpList.push({
          id: (index * 10000) + i,
          startTime: cutTime.startSec < v.start_sec ? v.start_sec : cutTime.startSec,
          endTime: v.end_sec < cutTime.endSec ? v.end_sec : cutTime.endSec,
        })
      })
    })
    let tmp: { id: number, startTime: number, endTime: number }[] = []
    tmpList.forEach((v) => {
      if (tmp.map((w) => w.id).includes(v.id)) {
        const target = tmp.find((w) => w.id === v.id)
        if (!target) {
          tmp.push(v)
          return
        }
        if ((target.endTime - target.startTime) < (v.endTime - v.startTime)) {
          tmp = tmp.map((w) => (w.id === v.id ? v : w))
        }
      } else {
        tmp.push(v)
      }
    })
    return tmp
  }, [customOriginTask, cutTimeList])

  const inRecodeTotalTime = useMemo(() => {
    if (!selectRecodeList.length) return 0
    let tmpArray: { id: number, startTime: number, endTime: number }[] = []
    selectRecodeList.forEach((v) => {
      if (tmpArray.find((w) => w.startTime >= v.startTime && w.startTime <= v.endTime && w.endTime >= v.endTime)) {
        const tmp = tmpArray.find((w) => w.startTime > v.startTime && w.startTime < v.endTime && w.endTime > v.endTime)
        if (!tmp) return
        tmpArray = tmpArray.map((w) => (w.id === tmp.id ? { ...w, startTime: v.startTime } : w))
        return
      }
      if (tmpArray.find((w) => w.startTime <= v.startTime && w.endTime >= v.startTime && w.endTime <= v.endTime)) {
        const tmp = tmpArray.find((w) => w.startTime < v.startTime && w.endTime > v.startTime && w.endTime < v.endTime)
        if (!tmp) return
        tmpArray = tmpArray.map((w) => (w.id === tmp.id ? { ...w, endTime: v.endTime } : w))
        return
      }
      if (tmpArray.find((w) => w.startTime >= v.startTime && w.endTime <= v.endTime)) {
        tmpArray = tmpArray.map((w) => (w.startTime >= v.startTime && w.endTime <= v.endTime ? { id: -1, startTime: 0, endTime: 0 } : w)).filter((w) => w.id >= 0)
        tmpArray.push(v)
        return
      }
      if (tmpArray.find((w) => w.startTime <= v.startTime && w.endTime >= v.endTime)) {
        return
      }
      tmpArray.push(v)
    })
    return tmpArray.reduce((s, v) => s + (v.endTime - v.startTime), 0)
  }, [selectRecodeList])

  const duplicateApplications = useMemo(() => {
    const tmpAppList: { title: string; time: number; icon: string | null; icon_color: string | null }[] = []
    const otherAppList = cutTimeList.map((v) => customOtherAppList.filter((w) => ((v.startSec <= w.end_sec) && (v.endSec >= w.start_sec)))).flat()
    customOriginTask.filter((v) => (v.end_sec - v.start_sec) > 0).forEach((v) => {
      otherAppList?.map((w) => {
        if (!(w.start_sec < v.end_sec && v.start_sec < w.end_sec)) return
        const tmpStartTime = w.start_sec < v.start_sec ? v.start_sec : w.start_sec
        const tmpEndTime = v.end_sec < w.end_sec ? v.end_sec : w.end_sec
        tmpAppList.push({
          title: w.service_name || "不明なアプリ",
          time: tmpEndTime - tmpStartTime,
          icon: w.icon,
          icon_color: w.icon_color,
        })
      })
    })
    let appList:{title: string, time: number, icon: string | null, icon_color: string | null}[] = []
    tmpAppList?.forEach((v) => {
      if (appList.map((w) => w.title).includes(v.title)) {
        appList = [...appList.map((x) => (x.title === v.title ? ({ ...x, title: x.title, time: x.time + v.time }) : x))]
      } else {
        appList.push({ ...v, title: v.title, time: v.time })
      }
    })
    return sortBy(appList, "time").reverse()
  }, [customOriginTask, cutTimeList])

  const modalTitle = useMemo(() => {
    if (type === "cut") {
      return "作業データの切り出し"
    }
    return "作業データの手動登録"
  }, [type])

  const modalButtonText = useMemo(() => {
    if (type === "cut") {
      return "作業データの切り出し"
    }
    return "登録"
  }, [type])

  const suggestWordList = useMemo(() => {
    const suggestWords:string[] = []
    cutTimeList.forEach((cutTime) => (
      customCalendarSchedules.filter((v) => (v.end_sec - v.start_sec) > 0).forEach((v) => {
        if (!(cutTime.startSec < v.end_sec && v.start_sec < cutTime.endSec)) return
        suggestWords.push(v.title)
      })
    ))
    return Array.from(new Set(suggestWords))
  }, [cutTimeList])

  const buildDisplayTime = (diffSeconds: number) => {
    if (diffSeconds > 0 && diffSeconds < 60) {
      return "1分未満"
    }
    if (secToHour(diffSeconds)) {
      return `${secToHour(diffSeconds)}時間${secToMinute(diffSeconds)}分`
    }
    return `${secToMinute(diffSeconds)}分`
  }

  const resetApp = () => {
    setLabelGroupList([...labelGroupListInit])
  }

  const inputName = (value: string) => {
    const tmpName = value.trim()
    if (tmpName) {
      setError([...error.filter((v) => v.key !== "name")])
    } else if (!error.filter((v) => v.key === "name").length) {
      setError([...error, { key: "name", text: "作業データ名を入力してください" }])
    }
    setName(tmpName)
  }

  const clickLabel = (labelGroup: LabelCommon, label: LabelCommon) => {
    if (!labelGroup && !label) {
      setSelectLabel({
        id: 0,
        name: "プロジェクトに属さない作業データ",
      })
      if (!name) {
        inputName("プロジェクトに属さない作業データ")
      }
      return
    }
    if (labelGroup.id === 1 && label.id === 1) {
      setSelectLabel({
        id: 1,
        name: "休憩",
      })
      if (!name) {
        inputName("休憩")
      }
      return
    }
    if (labelGroup.id === 2 && label.id === 2) {
      setSelectLabel({
        id: 2,
        name: "業務外",
      })
      if (!name) {
        inputName("業務外")
      }
      return
    }
    setSelectLabel({
      id: label.id,
      name: `${labelGroup.name}:${label.name}`,
    })
    if (!name) {
      inputName(`${labelGroup.name}:${label.name}`)
    }
    setIsDisplayLabelBox(false)
  }

  const resetParam = () => {
    setError([])
    setCutTimeList([{ startSec: 36000, endSec: 39600 }])
    setIsMoveDrag(false)
    setIsDisplayLabelBox(false)
    setSelectLabel({ id: 0, name: "" })
    setName("手動登録作業データ")
  }

  const closeModal = () => {
    closeCallback()
    resetParam()
  }

  const submit = () => {
    setIsProcessing(true)
    const errorList = []
    if (!isForceMode && !inRecodeTotalTime) {
      errorList.push({ key: "range", text: "選択範囲内に仕分けできる時間がありません。別の時間帯を選択してください。" })
    }
    if (!isForceMode && type === "cut" && originTaskTotalTime === inRecodeTotalTime) {
      errorList.push({ key: "range", text: "すべての記録を選択することはできません。" })
    }
    cutTimeList.forEach((cutTime) => {
      if (!isForceMode && cutTime.endSec - cutTime.startSec < (task.taskLabeling?.task_threshold || 60)) {
        errorList.push({ key: "range", text: `仕分けできる時間が不足しています。（登録に必要な時間：${secToMinute(Number(task.taskLabeling?.task_threshold || 60))}分以上）` })
      }
      if (cutTime.startSec >= cutTime.endSec) {
        errorList.push({ key: "range", text: "作業データの開始・終了時刻が不正です。" })
      }
    })
    if (!name) {
      errorList.push({ key: "name", text: "作業データ名を入力してください。" })
    }
    if (errorList.length) {
      setError(Array.from(new Set(errorList)))
      setIsProcessing(false)
      return
    }

    let tmpArray: { id: number, startTime: number, endTime: number }[] = []
    selectRecodeList.forEach((v) => {
      if (tmpArray.find((w) => w.startTime >= v.startTime && w.startTime <= v.endTime && w.endTime >= v.endTime)) {
        const tmp = tmpArray.find((w) => w.startTime > v.startTime && w.startTime < v.endTime && w.endTime > v.endTime)
        if (!tmp) return
        tmpArray = tmpArray.map((w) => (w.id === tmp.id ? { ...w, startTime: v.startTime } : w))
        return
      }
      if (tmpArray.find((w) => w.startTime <= v.startTime && w.endTime >= v.startTime && w.endTime <= v.endTime)) {
        const tmp = tmpArray.find((w) => w.startTime < v.startTime && w.endTime > v.startTime && w.endTime < v.endTime)
        if (!tmp) return
        tmpArray = tmpArray.map((w) => (w.id === tmp.id ? { ...w, endTime: v.endTime } : w))
        return
      }
      if (tmpArray.find((w) => w.startTime >= v.startTime && w.endTime <= v.endTime)) {
        tmpArray = tmpArray.map((w) => (w.startTime >= v.startTime && w.endTime <= v.endTime ? { id: -1, startTime: 0, endTime: 0 } : w)).filter((w) => w.id >= 0)
        tmpArray.push(v)
        return
      }
      if (tmpArray.find((w) => w.startTime <= v.startTime && w.endTime >= v.endTime)) {
        return
      }
      tmpArray.push(v)
    })

    cutCallback(
      name, selectLabel.id, tmpArray, cutTimeList, !!selectLabel.name, originTask?.task_id || null, type,
      () => {
        closeCallback()
        resetParam()
        setIsProcessing(false)
      },
    )
  }

  const rulerBlock = () => (
    <div
      className="timeline_ruler_area"
      style={{
        width: "100%",
        marginLeft: 0,
        position: "absolute",
        top: 0,
        left: 0,
        height: "42px",
      }}
    >
      {
        Array((TIME_LIST.length * 2) - 1).fill(null).map((_v, i) => (
          <div key={`ruler_${i + 0}`} />
        ))
      }
      <div />
    </div>
  )

  const labelBlock = () => (
    <div
      className="timeline_label_area"
      style={{
        width: "100%",
        position: "relative",
        height: "14px",
        marginTop: "4px",
        marginBottom: "4px",
        fontSize: "10px",
        color: "#666666",
      }}
    >
      {
        TIME_LIST.map((v, i) => (
          <div
            key={v}
            style={{
              position: "absolute",
              left: `${(i / 24) * 100}%`,
              transform: "translateX(-50%)",
            }}
          >
            {v}
          </div>
        ))
      }
    </div>
  )

  const selectCutStartTime = (hour: number, minutes: number, index: number) => {
    const tmpSec = (hour * 3600) + (minutes * 60)
    if (tmpSec > cutTimeList[index].endSec) {
      setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, startSec: v.endSec } : v)))
      return
    }
    setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, startSec: tmpSec } : v)))
  }

  const selectCutEndTime = (hour: number, minutes: number, index: number) => {
    const tmpSec = (hour * 3600) + (minutes * 60)
    if (tmpSec > 86400 + 18000) {
      setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, endSec: 86400 + 18000 } : v)))
      return
    }
    if (tmpSec < cutTimeList[index].startSec) {
      setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, endSec: v.startSec } : v)))
      return
    }
    setCutTimeList(cutTimeList.map((v, i) => (i === index ? { ...v, endSec: tmpSec } : v)))
  }

  const hourList = (selectType: string, index: number) => {
    if (selectType === "start") {
      let tmpHourCount = secToHour(cutTimeList[index].endSec)
      if (tmpHourCount > 28) {
        tmpHourCount = 28
      } else if (tmpHourCount < 5) {
        tmpHourCount += 24
      }
      return Array(((tmpHourCount - 5) + 1)).fill(null).map((_v, i) => i + 5)
    }
    const startHour = secToHour(cutTimeList[index].startSec)
    return Array(29 - (startHour - 1)).fill(null).map((_v, i) => i + startHour)
  }

  const minutesList = (selectType: string, index: number) => {
    if (selectType === "start") {
      if (secToHour(cutTimeList[index].startSec) === secToHour(cutTimeList[index].endSec)) {
        return Array(((Number(secToMinute(cutTimeList[index].endSec))) + 1)).fill(null).map((_v, i) => `00${i}`.slice(-2))
      }
      return Array(60).fill(null).map((_v, i) => `00${i}`.slice(-2))
    }
    if (secToHour(cutTimeList[index].endSec) > 28) {
      return ["00"]
    }
    if (secToHour(cutTimeList[index].startSec) === secToHour(cutTimeList[index].endSec)) {
      const startMinutesCount = Number(secToMinute(cutTimeList[index].startSec))
      return Array(60 - startMinutesCount).fill(null).map((_v, i) => `00${(i + startMinutesCount)}`.slice(-2))
    }
    return Array(60).fill(null).map((_v, i) => `00${i}`.slice(-2))
  }

  const clickSchedule = (title: string, startTime: number, endTime: number) => {
    if (!name) inputName(title)
    setCutTimeList([{ startSec: startTime, endSec: endTime }])
  }

  const reset = () => {
    setIsMoveDrag(false)
    setDragType("")
    setBasePosition(null)
    setBaseStartTime(0)
    setBaseEndTime(0)
    setBaseIndex(null)
  }

  const endDrag = () => {
    reset()
  }

  const startDrag = (clickPosition: number, tmpDragType: string, index: number) => {
    setIsMoveDrag(true)
    if (tmpDragType === "start") {
      setDragType("start")
    } else if (tmpDragType === "end") {
      setDragType("end")
    } else {
      setDragType("move")
    }
    setBasePosition(clickPosition)
    setBaseStartTime(cutTimeList[index].startSec)
    setBaseEndTime(cutTimeList[index].endSec)
    setBaseIndex(index)
  }

  const moveOrDragSchedule = (clickPosition: number) => {
    if (baseIndex === null) return
    const diff = clickPosition - (basePosition || 0)
    const tmpSec = Math.ceil((diff / clickRef.current.clientWidth) * 288) * 300
    if (dragType === "start") {
      if (baseStartTime + tmpSec < 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, startSec: 18000 } : v)))
      } else if (baseStartTime + tmpSec > cutTimeList[baseIndex].endSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, startSec: v.endSec } : v)))
      } else {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, startSec: baseStartTime + tmpSec } : v)))
      }
    }
    if (dragType === "end") {
      if (baseEndTime + tmpSec > 86400 + 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, endSec: 86400 + 18000 } : v)))
      } else if (baseEndTime + tmpSec < cutTimeList[baseIndex].startSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, endSec: v.startSec } : v)))
      } else {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { ...v, endSec: baseEndTime + tmpSec } : v)))
      }
    }
    if (dragType === "move") {
      if (baseStartTime + tmpSec < 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: 18000, endSec: baseEndTime + tmpSec } : v)))
      } else if (baseEndTime + tmpSec > 86400 + 18000) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: baseStartTime + tmpSec, endSec: 86400 + 18000 } : v)))
      } else if (baseEndTime + tmpSec < cutTimeList[baseIndex].startSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: baseStartTime + tmpSec, endSec: v.startSec } : v)))
      } else if (baseStartTime + tmpSec > cutTimeList[baseIndex].endSec) {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: v.endSec, endSec: baseEndTime + tmpSec } : v)))
      } else {
        setCutTimeList(cutTimeList.map((v, i) => (i === baseIndex ? { startSec: baseStartTime + tmpSec, endSec: baseEndTime + tmpSec } : v)))
      }
    }
  }

  const buildBlock = (targetStartSec: number, targetEndSec: number, index: number) => {
    const startPosition = ((targetStartSec - 18000) / 86400) * 100
    const blockWidth = ((targetEndSec - targetStartSec) / 86400) * 100
    return (
      <>
        <div
          style={{
            position: "absolute",
            left: `calc(${startPosition}% - 10px)`,
            top: "50%",
            width: "10px",
            height: "40px",
            padding: 0,
            borderRadius: "4px 0 0 4px",
            transform: "translateY(-50%)",
            zIndex: 1,
            cursor: isMoveDrag ? "grabbing" : "col-resize",
          }}
          onMouseDown={(e) => startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "start", index)}
          role="none"
        >
          <div
            style={{
              position: "absolute",
              top: "-2px",
              left: "0",
              width: "10px",
              height: "44px",
              backgroundColor: "#0560D4",
              borderRadius: "4px 0 0 4px",
              zIndex: 1,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "5px",
              left: "4px",
              width: "2px",
              height: "30px",
              borderLeft: "dashed 2px rgba(255, 255, 255, 0.3)",
              zIndex: 2,
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            left: `${startPosition}%`,
            top: "50%",
            width: `${blockWidth}%`,
            height: "44px",
            padding: 0,
            backgroundColor: "rgba(5, 96, 212, 0.2)",
            borderTop: "solid 2px #0560D4",
            borderBottom: "solid 2px #0560D4",
            transform: "translateY(-50%)",
            zIndex: 1,
            cursor: isMoveDrag ? "grabbing" : "move",
          }}
          onMouseDown={(e) => startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "move", index)}
          role="none"
        />
        <div
          style={{
            position: "absolute",
            left: `${startPosition + blockWidth}%`,
            top: "50%",
            width: "10px",
            height: "40px",
            padding: 0,
            borderRadius: "0 4px 4px 0",
            transform: "translateY(-50%)",
            zIndex: 1,
            cursor: isMoveDrag ? "grabbing" : "col-resize",
          }}
          onMouseDown={(e) => startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "end", index)}
          role="none"
        >
          <div
            style={{
              position: "absolute",
              top: "-2px",
              left: "0",
              width: "10px",
              height: "44px",
              backgroundColor: "#0560D4",
              borderRadius: "0 4px 4px 0",
              zIndex: 1,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "5px",
              left: "4px",
              width: "2px",
              height: "30px",
              borderLeft: "dashed 2px rgba(255, 255, 255, 0.3)",
              zIndex: 2,
            }}
          />
        </div>
      </>
    )
  }

  const addPeriod = () => {
    setCutTimeList([...cutTimeList, { startSec: 36000, endSec: 39600 }])
  }

  const delPeriod = (index: number) => {
    setCutTimeList(cutTimeList.filter((_v, i) => i !== index))
  }

  useEffect(() => {
    setLabelGroupList(task.taskLabeling?.label_groups.filter((v) => !v.disabled) || [])
    setLabelGroupListInit(task.taskLabeling?.label_groups.filter((v) => !v.disabled) || [])
  }, [task])

  useEffect(() => {
    resetApp()
  }, [isDisplayLabelBox])

  useEffect(() => {
    setIsChangeRecodeTime(true)
  }, [inRecodeTotalTime, isForceMode, cutTimeList])

  useEffect(() => {
    resetApp()
    if (!isChangeRecodeTime) return
    if (!isForceMode && !inRecodeTotalTime) {
      setError([...error, { key: "range", text: "選択範囲内に仕分けできる時間がありません。別の時間帯を選択してください。" }])
    } else if (!isForceMode && type === "cut" && originTaskTotalTime === inRecodeTotalTime) {
      setError([...error, { key: "range", text: "すべての記録を選択することはできません。" }])
    } else if (!isForceMode && inRecodeTotalTime < (task.taskLabeling?.task_threshold || 60)) {
      setError([...error, { key: "range", text: `仕分けできる時間が不足しています。（登録に必要な時間：${secToMinute(Number(task.taskLabeling?.task_threshold || 60))}分以上）` }])
    } else if (isForceMode && !diffNowToDate(date) && Math.max(...cutTimeList.map((v) => v.endSec)) - (dateToTodayElapsedSeconds(nowDate("YYYY/MM/DD H:m:s"))) > 0) {
      setError([...error, { key: "range", text: "現在時刻より後の時刻が指定されています。" }])
    } else {
      setError([...error.filter((v) => v.key !== "range")])
    }
    setIsChangeRecodeTime(false)
  }, [isChangeRecodeTime])

  useEffect(() => {
    if (timeList.length) {
      setCutTimeList(timeList)
    } else {
      setCutTimeList([{ startSec: 36000, endSec: 39600 }])
    }
  }, [timeList])

  useEffect(() => {
    setError([...error.filter((v) => v.key !== "range")])
    if (overwrite && selectLabel.id === 1 && type === "create") {
      setIsForceMode(true)
    } else {
      setIsForceMode(false)
    }
  }, [selectLabel])

  return (
    <>
      <Modal
        title={modalTitle}
        show={isDisplay}
        wrapperClass="w900"
        closeCallback={closeModal}
        closeable={!isProcessing}
      >
        <>
          <div
            role="none"
            onMouseMove={(e) => isMoveDrag && moveOrDragSchedule(e.clientX - clickRef.current.getBoundingClientRect().left)}
            onMouseUp={() => isMoveDrag && endDrag()}
            onMouseLeave={() => isMoveDrag && endDrag()}
          >
            <div className="header_area" style={{ userSelect: "none" }}>
              <div className="text_area">
                <div>
                  {
                    originTask?.task_name ? (
                      <>
                        {`作業データ名「${originTask?.task_name}」の`}
                      </>
                    ) : (
                      <></>
                    )
                  }
                  選択した範囲の記録を切り出して、新しい作業データを生成します。
                </div>
              </div>
            </div>
            <div className="item" style={{ userSelect: "none" }}>
              <div className="title" style={{ justifyContent: "space-between" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  作業データ名
                  <div className="required">
                    必須
                  </div>
                </div>
              </div>
              <div className="detail equal_space row_1 labeling_field">
                <input
                  type="text"
                  value={name || ""}
                  onChange={(e) => inputName(e.target.value)}
                  placeholder="作業データ名を入力してください"
                />
                <div className="button_area">
                  {
                    selectLabel.name ? (
                      <div className="label">
                        {selectLabel.name}
                        <span
                          className="delete"
                          onClick={() => setSelectLabel({ id: 0, name: "" })}
                          role="none"
                        >
                          <FontAwesomeIcon icon={['fas', 'times']} />
                        </span>
                      </div>
                    ) : (
                      <>
                        <div
                          className="add_label"
                          onClick={() => setIsDisplayLabelBox(true)}
                          role="none"
                        >
                          業務仕分けする
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
              {
                type === "create" && suggestWordList.length ? (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      margin: "8px 0",
                      fontSize: "10px",
                      color: "#666666",
                    }}
                  >
                    もしかして：
                    {
                      suggestWordList.map((v) => (
                        <div
                          onClick={() => inputName(v)}
                          role="none"
                          style={{
                            textDecoration: "underline",
                            color: "#4372ba",
                            cursor: "pointer",
                          }}
                        >
                          {v}
                        </div>
                      ))
                    }
                    ですか？
                  </div>
                ) : (
                  <></>
                )
              }
              {
                error.find((v) => v.key === "name") && (
                  <div className="error">
                    {error.find((v) => v.key === "name")?.text}
                  </div>
                )
              }
            </div>
            <div className="item" style={{ userSelect: "none" }}>
              <div className="title" style={{ justifyContent: "space-between" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  範囲選択
                  <div className="required">
                    必須
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#06a2a2",
                    }}
                  >
                    {
                      type === "cut" ? (
                        <>
                          選択範囲内の分割できる時間
                        </>
                      ) : (
                        <>
                          選択範囲内の仕分けできる時間
                        </>
                      )
                    }
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "#666666",
                    }}
                  >
                    {
                      isForceMode ? (
                        <>
                          {buildDisplayTime(cutTimeList.reduce((s, v) => s + (v.endSec - v.startSec), 0))}
                        </>
                      ) : (
                        <>
                          {buildDisplayTime(inRecodeTotalTime)}
                        </>
                      )
                    }
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "4px", justifyContent: "center", flexFlow: "column", position: "relative" }}>
                {
                  cutTimeList.map((v, i) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <div style={{ display: "flex", gap: "4px", alignItems: "center", fontWeight: "bold" }}>
                        <div
                          style={{
                            width: "50px",
                          }}
                        >
                          <SelectAndInput
                            title="start_hours"
                            originList={hourList("start", i).map(String)}
                            defaultValue={secToHour(v.startSec).toString()}
                            callback={(value) => selectCutStartTime(Number(value), Number(secToMinute(v.startSec)), i)}
                          />
                        </div>
                        :
                        <div
                          style={{
                            width: "50px",
                          }}
                        >
                          <SelectAndInput
                            title="start_minutes"
                            originList={minutesList("start", i).map(String)}
                            defaultValue={`00${secToMinute(v.startSec)}`.slice(-2)}
                            callback={(value) => selectCutStartTime(Number(secToHour(v.startSec)), Number(value), i)}
                            zeroPadding
                          />
                        </div>
                      </div>
                      ~
                      <div style={{ display: "flex", gap: "4px", alignItems: "center", fontWeight: "bold" }}>
                        <div
                          style={{
                            width: "50px",
                          }}
                        >
                          <SelectAndInput
                            title="end_hours"
                            originList={hourList("end", i).map(String)}
                            defaultValue={secToHour(v.endSec).toString()}
                            callback={(value) => selectCutEndTime(Number(value), Number(secToMinute(v.startSec)), i)}
                          />
                        </div>
                        :
                        <div
                          style={{
                            width: "50px",
                          }}
                        >
                          <SelectAndInput
                            title="end_minutes"
                            originList={minutesList("end", i).map(String)}
                            defaultValue={`00${secToMinute(v.endSec)}`.slice(-2)}
                            callback={(value) => selectCutEndTime(Number(secToHour(v.endSec)), Number(value), i)}
                            zeroPadding
                          />
                        </div>
                      </div>
                      の範囲が選択されています
                      {
                        cutTimeList.length > 1 && (
                          <div
                            onClick={() => delPeriod(i)}
                            role="none"
                            style={{
                              color: "#FF0000",
                              cursor: "pointer",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                          </div>
                        )
                      }
                    </div>
                  ))
                }
                {
                  type === "create" && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-4px",
                        right: "0",
                      }}
                    >
                      <button
                        type="button"
                        onClick={addPeriod}
                        style={{
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                        }}
                      >
                        選択範囲を増やす
                      </button>
                    </div>
                  )
                }
                {
                  isForceMode && (
                    <Tips
                      description="指定範囲で作業データを作成し、他作業データを強制的に上書きします"
                      className=""
                      icon={false}
                    >
                      <>
                        <div
                          style={{
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "#FF0000",
                          }}
                        >
                          [上書きモード]
                          <FontAwesomeIcon icon={["far", "question-circle"]} />
                        </div>
                      </>
                    </Tips>
                  )
                }
              </div>
              <div
                className="timeline"
                style={{
                  marginBottom: "0px",
                  height: "44px",
                }}
              >
                <div
                  ref={clickRef}
                  className="timeline_row"
                  style={{
                    borderBottom: "none",
                    height: "42px",
                  }}
                >
                  {rulerBlock()}
                  {
                    isForceMode && (
                      <div
                        className="timeline_bar force"
                        style={{
                          left: 0,
                          width: !diffNowToDate(date) ? `${((dateToTodayElapsedSeconds(nowDate("YYYY/MM/DD H:m:s")) - 18000) / 86400) * 100}%` : "100%",
                        }}
                      />
                    )
                  }
                  {
                    !isForceMode && customOriginTask.map((value) => (
                      <div
                        key={`timeline_modal_${value.start_time}`}
                        className={
                          `timeline_bar ${type === "create" ? "create" : ""}`
                        }
                        style={{
                          left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                          width:
                          `${(((value.end_sec - value.start_sec) / 86400)) * 100}%`,
                        }}
                      />
                    ))
                  }
                  {
                    !isForceMode && type === "create" && customOtherAppList.map((value) => (
                      <React.Fragment key={`timeline_modal_duplicate_${value.start_time}`}>
                        <div
                          className="timeline_bar duplicate"
                          style={{
                            left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                            width:
                            `${(((value.end_sec - value.start_sec) / 86400)) * 100}%`,
                          }}
                        />
                      </React.Fragment>
                    ))
                  }
                  {
                    cutTimeList.map((v, i) => (
                      buildBlock(v.startSec, v.endSec, i)
                    ))
                  }
                </div>
              </div>
              {labelBlock()}
              {
                error.find((v) => v.key === "range")?.text && (
                  <div className="error">
                    {error.find((v) => v.key === "range")?.text}
                  </div>
                )
              }
              {
                task.taskLabeling?.calendar.schedules.length ? (
                  <>
                    <div className="timeline calendar">
                      <div className="timeline_row" style={{ height: "38px" }}>
                        {rulerBlock()}
                        {
                          customCalendarSchedules.map((value) => (
                            <React.Fragment key={`calendar_modal_${value.start_time}`}>
                              <div
                                className="timeline_item"
                                style={{
                                  left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                                  width:
                                  // eslint-disable-next-line max-len
                                  `${(((value.end_sec - value.start_sec) / 86400)) * 100}%`,
                                }}
                                role="none"
                                onClick={
                                  () => clickSchedule(value.title, value.start_sec, value.end_sec)
                                }
                              >
                                <Tips
                                  description={`${format(value.start_time, "HH時mm分")} ~ ${format(value.end_time, "HH時mm分")} ${value.title}`}
                                  className=""
                                  icon={false}
                                >
                                  <>
                                  </>
                                </Tips>
                              </div>
                            </React.Fragment>
                          ))
                        }
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )
              }
            </div>
            {
              type === "create" && (
                <>
                  <div className="item" style={{ userSelect: "none" }}>
                    {
                      !!duplicateApplications.length && (
                        <>
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                              backgroundColor: "#F5F5F5",
                              padding: "8px",
                              borderRadius: "4px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                marginBottom: "8px",
                                textAlign: "center",
                                fontWeight: "bold",
                              }}
                            >
                              以下の仕分け対象外アプリケーションが選択時間内に含まれています
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                marginBottom: "8px",
                                textAlign: "center",
                                color: "#666666",
                              }}
                            >
                              ※作業データ登録が完了するまで、他のユーザーは以下の業務内容を知ることができません。
                            </div>
                            <div
                              style={{
                                border: "dashed 1px #b4e5e2",
                                backgroundColor: "#e2f5f3",
                                padding: "8px 8px 4px",
                                borderRadius: "4px",
                                maxHeight: "100px",
                                overflowY: "auto",
                              }}
                            >
                              {
                                duplicateApplications.map((value) => (
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      gap: "8px",
                                      marginBottom: "4px",
                                      fontWeight: "bold",
                                    }}
                                    key={`${value.title}`}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <div
                                        className="icon_area"
                                        style={{
                                          color: value.icon_color || (value.title ? "#008800" : "#FF0000"),
                                          fontSize: "14px",
                                        }}
                                      >
                                        {
                                          value.icon ? (
                                            <FontAwesomeIcon
                                              icon={[value.icon.split(" ")[0] as IconPrefix, value.icon.split(" ")[1] as IconName]}
                                              fixedWidth
                                            />
                                          ) : (
                                            <FontAwesomeIcon
                                              icon={value.title ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                              fixedWidth
                                            />
                                          )
                                        }
                                      </div>
                                      {value.title}
                                    </div>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        </>
                      )
                    }
                  </div>
                </>
              )
            }
          </div>
        </>
        <>
          <button
            type="button"
            className="button cancel"
            onClick={closeModal}
            disabled={isProcessing}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="button"
            onClick={submit}
            disabled={!!error.length || !name || isProcessing}
          >
            {
              isProcessing ? (
                <>
                  <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                </>
              ) : (
                <>
                  {modalButtonText}
                </>
              )
            }
          </button>
        </>
      </Modal>
      {
        isDisplayLabelBox && (
          <AddLabelBigModal
            taskStatus={{ ids: [0], x: "auto", y: "auto" }}
            labelGroupOriginList={login.feature_config?.high_volume ? [] : labelGroupList}
            callbackLabels={(taskIds, labelGroup, labels) => clickLabel(labelGroup, labels[0])}
            closeCallback={() => setIsDisplayLabelBox(false)}
            currentMemberId={currentMemberId}
            taskDetails={[]}
          />
        )
      }
    </>
  )
}

export default CutTaskModal
